import { Play } from 'lucide-react'
import { useEffect, useRef, useState, type RefObject } from 'react'

import { cn } from '@/lib/utils'

export type VideoThumbnail = { src: string; width: number; height: number }

/** Calls `onLeave` each time `ref`'s element scrolls fully out of view, while
    `active`. Clipping ancestors count, so a carousel slide swiped out of its
    viewport leaves too. */
export function useOnLeaveViewport(
  ref: RefObject<Element | null>,
  active: boolean,
  onLeave: () => void,
) {
  const onLeaveRef = useRef(onLeave)
  onLeaveRef.current = onLeave
  useEffect(() => {
    const element = ref.current
    if (!active || !element) return
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) onLeaveRef.current()
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, active])
}

/** Clickable thumbnail with a centered play button; the click swaps it for the
    video, in the same frame (sound on, native controls). Nothing is downloaded
    before the click: the <video> only mounts once played. It pauses when
    scrolled out of view. The video box is always 16:9, whatever the
    thumbnail's ratio. `srcMobile` is served under the `md` breakpoint.
    `className` frames the player (border, shadow, radius). */
export function InlineVideo({
  title,
  thumbnail,
  src,
  srcMobile,
  className,
}: {
  title: string
  thumbnail: VideoThumbnail
  src: string
  srcMobile?: string
  className?: string
}) {
  const [playing, setPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Runs in the click's commit, so the play() call still carries the user
  // gesture Safari requires for sound. A refusal leaves the native controls
  // up for the visitor to press play: nothing to recover.
  useEffect(() => {
    const video = videoRef.current
    if (!playing || !video) return
    video.focus({ preventScroll: true })
    video.play().catch(() => {})
  }, [playing])

  useOnLeaveViewport(videoRef, playing, () => videoRef.current?.pause())

  return (
    <div
      className={cn('relative w-full overflow-hidden rounded-3xl', className)}
    >
      {playing ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          ref={videoRef}
          src={srcMobile ? undefined : src}
          aria-label={title}
          controls
          playsInline
          className="aspect-video w-full bg-black"
        >
          {/* Mobile cut first: a browser that ignores `media` takes the
              lighter file. */}
          {srcMobile && (
            <>
              <source
                src={srcMobile}
                media="(max-width: 767px)"
                type="video/mp4"
              />
              <source src={src} type="video/mp4" />
            </>
          )}
        </video>
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="group relative block w-full cursor-pointer"
        >
          <img
            src={thumbnail.src}
            alt={title}
            width={thumbnail.width}
            height={thumbnail.height}
            loading="lazy"
            decoding="async"
            className="w-full transition duration-300 group-hover:scale-[1.015]"
          />
          {/* Play overlay: dim the thumbnail, centered play button. */}
          <span className="absolute inset-0 flex items-center justify-center bg-black/10 transition group-hover:bg-black/20">
            <span className="bg-linear-to-r/srgb flex size-16 items-center justify-center rounded-full border border-[#0211D9] from-[#0211D9] to-[#2736F8] text-primary-foreground shadow-lg transition group-hover:brightness-110">
              <Play className="size-7 translate-x-0.5 fill-current" />
            </span>
          </span>
        </button>
      )}
    </div>
  )
}
